import type { Clave, Traductor, Valores } from './traducir';
import { es } from './catalogos/es';

/**
 * La parte del `ProblemDetails` (RFC 9457) que el formulario necesita. La API
 * manda más campos —`type`, `title`, `status`, `correlationId`— pero acá solo
 * importan el código general y los códigos por campo.
 *
 *   { "code": "request.validation",
 *     "errors": { "mensaje": ["validation.required"] } }
 */
export interface ProblemaApi {
  code?: string;
  errors?: Record<string, string[]>;
  /**
   * Lo que la orden rechazó, **todo junto** (#130): los códigos de cada problema,
   * el principal incluido. Solo lo traen los 409 de la orden; en los demás falta.
   *
   *   { "code": "service_order.falta_cliente",
   *     "problems": ["service_order.falta_cliente", "service_order.falta_folio"] }
   */
  problems?: string[] | null;
}

/**
 * Convierte un código de la API en su clave del catálogo: `validation.required`
 * pasa a `error.validation.required`.
 *
 * Los códigos son cadenas que llegan por red, así que TypeScript no puede
 * garantizar que existan en el catálogo. Se comprueba contra el español —que es
 * la fuente de verdad— y si no está, se cae a `error.desconocido` en vez de
 * mostrar la clave cruda. Un código nuevo en la API sin traducción se ve como
 * un mensaje genérico, no como un `error.validation.foo` en pantalla.
 */
function claveDe(codigo: string): Clave {
  const clave = `error.${codigo}`;
  return clave in es ? (clave as Clave) : 'error.desconocido';
}

/**
 * El texto de un código de error suelto, en el idioma del traductor. Con
 * `valores`, rellena sus huecos: `{customerName}` de `service_order.cliente_inactivo`.
 */
export function traducirError(codigo: string, t: Traductor, valores?: Valores): string {
  return t(claveDe(codigo), valores);
}

/**
 * Los datos sueltos que el problema trae junto al código —`customerName`, por
 * ejemplo—, para los huecos de su texto. Solo textos y números: una lista o un
 * objeto no se escriben en una frase.
 */
function datosDe(problema: ProblemaApi): Valores {
  return Object.fromEntries(
    Object.entries(problema as Record<string, unknown>).filter(
      (dato): dato is [string, string | number] => typeof dato[1] === 'string' || typeof dato[1] === 'number',
    ),
  );
}

/**
 * El texto del aviso para un error que no es de un campo: el `409` de una orden
 * que no se puede entregar, un código repetido sin campo donde marcarlo.
 *
 * Si el problema trae `problems`, el aviso los dice **todos de una vez** —«para
 * entregarla falta el cliente y el folio»—, para que la persona no corrija uno,
 * reintente y descubra el siguiente. Si no, es el texto de `code`.
 */
export function traducirProblema(problema: ProblemaApi, t: Traductor): string {
  // Cada texto es una frase entera: se separan con punto, como el aviso de
  // «campos fuera de pantalla» en `useFormulario`. El punto final de cada una se
  // quita antes, o las que ya lo traen quedarían con dos.
  if (problema.problems?.length) {
    return problema.problems.map((codigo) => traducirError(codigo, t).replace(/\.$/, '')).join('. ');
  }

  return traducirError(problema.code ?? '', t, datosDe(problema));
}

/**
 * Traduce los errores por campo de una respuesta `400`.
 *
 * Devuelve el mismo diccionario, pero con textos en vez de códigos, listo para
 * pintar bajo cada recuadro:
 *
 *   { mensaje: ['El campo es obligatorio'] }
 *
 * Es el único lugar donde un código de validación se convierte en texto. **La
 * API no sabe en qué idioma está la pantalla** —esa es la regla de B-21— y por
 * eso el idioma entra acá, con el traductor, y no viaja en la petición.
 */
export function erroresPorCampo(problema: ProblemaApi, t: Traductor): Record<string, string[]> {
  const resultado: Record<string, string[]> = {};
  for (const [campo, codigos] of Object.entries(problema.errors ?? {})) {
    resultado[campo] = codigos.map((codigo) => traducirError(codigo, t));
  }
  return resultado;
}
